import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

interface Version { id: string; file_path: string; label: string | null; auto: number; created_at: string }
interface Branch { id: string; name: string; project_dir: string; created_at: string }

export function VersionHistory() {
  const { openTabs, activeTabId, markDirty } = useAppStore()
  const activeTab = openTabs.find(t => t.id === activeTabId)
  const [tab, setTab] = useState<'history' | 'branches'>('history')
  const [versions, setVersions] = useState<Version[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [branches, setBranches] = useState<Branch[]>([])
  const [newBranchName, setNewBranchName] = useState('')
  const [selectedProject, setSelectedProject] = useState('')
  const [projects, setProjects] = useState<string[]>([])
  const [creating, setCreating] = useState(false)

  const load = useCallback(() => {
    if (!activeTab) return
    authFetch(apiUrl(`/api/versions?filePath=${encodeURIComponent(activeTab.path)}`))
      .then(r => r.json()).then(setVersions).catch(() => {})
  }, [activeTab?.path])

  const loadBranches = useCallback(() => {
    authFetch(apiUrl('/api/versions/branches'))
      .then(r => r.json()).then(d => setBranches(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])

  const loadProjects = useCallback(() => {
    authFetch(apiUrl('/api/files/list'))
      .then(r => r.json())
      .then(tree => {
        const folders = (tree ?? []).filter((f: any) => f.type === 'folder').map((f: any) => f.name)
        setProjects(folders)
        if (folders.length > 0 && !selectedProject) setSelectedProject(folders[0])
      }).catch(() => {})
  }, [selectedProject])

  useEffect(() => { load(); loadBranches(); loadProjects() }, [load, loadBranches, loadProjects])

  const viewVersion = async (id: string) => {
    setSelected(id)
    try {
      const res = await authFetch(apiUrl(`/api/versions/${id}`))
      const data = await res.json()
      setPreviewContent(data.content)
    } catch {}
  }

  const restoreVersion = () => {
    if (!previewContent || !activeTabId) return
    markDirty(activeTabId, previewContent)
    setPreviewContent(null)
    setSelected(null)
  }

  const createBranch = async () => {
    if (!newBranchName.trim() || !selectedProject) return
    setCreating(true)
    try {
      await authFetch(apiUrl('/api/versions/branches'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newBranchName.trim(), projectDir: selectedProject }),
      })
      setNewBranchName('')
      loadBranches()
    } catch {} finally { setCreating(false) }
  }

  const restoreBranch = async (b: Branch) => {
    if (!confirm(`This will overwrite files in "/${b.project_dir}" with this branch snapshot. Continue?`)) return
    await authFetch(apiUrl(`/api/versions/branches/${b.id}/restore`), { method: 'POST' })
    // Reload any open tabs that belong to this project
    const { openTabs } = useAppStore.getState()
    for (const tab of openTabs) {
      if (tab.path.includes(b.project_dir)) {
        try {
          const res = await authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(tab.path.replace(/^\//, ''))}`))
          const data = await res.json()
          if (data.content !== undefined) {
            useAppStore.getState().markDirty(tab.id, data.content)
            useAppStore.getState().markClean(tab.id)
          }
        } catch {}
      }
    }
    window.dispatchEvent(new CustomEvent('pulse:files-changed'))
  }

  const deleteBranch = async (id: string) => {
    await authFetch(apiUrl(`/api/versions/branches/${id}`), { method: 'DELETE' })
    loadBranches()
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header with tabs */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 14 }}>⏱</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)' }}>Versions</span>
        <div style={{ flex: 1 }} />
        {(['history', 'branches'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '2px 8px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700,
            border: tab === t ? '1px solid var(--accent)' : '1px solid var(--border-2)',
            background: tab === t ? 'rgba(124,110,245,0.1)' : 'none',
            color: tab === t ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
          }}>{t === 'history' ? '⏱ History' : '🌿 Branches'}</button>
        ))}
      </div>

      {tab === 'history' && <HistoryTab activeTab={activeTab} versions={versions} selected={selected} previewContent={previewContent} viewVersion={viewVersion} restoreVersion={restoreVersion} load={load} />}

      {tab === 'branches' && (
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Create branch */}
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: 6, textTransform: 'uppercase' }}>Save project state as branch</div>
            <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} style={{ width: '100%', padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 10, outline: 'none', marginBottom: 6 }}>
              {projects.length === 0 && <option value="">No projects found</option>}
              {projects.map(p => <option key={p} value={p}>📁 /{p}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 4 }}>
              <input value={newBranchName} onChange={e => setNewBranchName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') createBranch() }} placeholder="Branch name (e.g. Idea B)" style={{ flex: 1, padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 10, outline: 'none' }} />
              <button onClick={createBranch} disabled={creating || !newBranchName.trim() || !selectedProject} style={{ ...btnStyle, opacity: newBranchName.trim() && selectedProject ? 1 : 0.4 }}>🌿 Save</button>
            </div>
          </div>

          <div style={{ height: 1, background: 'var(--border)' }} />

          {/* Branch list */}
          {branches.length === 0 && (
            <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 10 }}>
              No branches yet. Save your current project state to try a different approach.
            </div>
          )}
          {branches.map(b => {
            const date = new Date(b.created_at)
            return (
              <div key={b.id} style={{ padding: '10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12 }}>🌿</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-0)' }}>{b.name}</span>
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
                  {date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {b.project_dir && <span> · /{b.project_dir}</span>}
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                  <button onClick={() => restoreBranch(b)} style={{ ...btnStyle, background: 'rgba(95,212,164,0.1)', color: 'var(--accent-2)', borderColor: 'var(--accent-2)' }}>↺ Restore</button>
                  <button onClick={() => deleteBranch(b.id)} style={{ ...btnStyle, color: 'var(--red)', borderColor: 'var(--red)' }}>✕ Delete</button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function HistoryTab({ activeTab, versions, selected, previewContent, viewVersion, restoreVersion, load }: {
  activeTab: any; versions: Version[]; selected: string | null; previewContent: string | null
  viewVersion: (id: string) => void; restoreVersion: () => void; load: () => void
}) {
  if (!activeTab) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        Open a file to see its version history
      </div>
    )
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Preview pane */}
      {previewContent !== null && (
        <div style={{ borderBottom: '1px solid var(--border)', flexShrink: 0, maxHeight: 200, overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: 'rgba(124,110,245,0.06)' }}>
            <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700 }}>Preview</span>
            <div style={{ flex: 1 }} />
            <button onClick={restoreVersion} style={{ ...btnStyle, background: 'rgba(95,212,164,0.15)', color: 'var(--accent-2)' }}>✓ Restore</button>
            <button onClick={() => { setPreviewContent(null); setSelected(null) }} style={btnStyle}>✕ Close</button>
          </div>
          <pre style={{ padding: '8px 14px', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', margin: 0, whiteSpace: 'pre-wrap' }}>
            {previewContent.slice(0, 3000)}{previewContent.length > 3000 ? '\n...' : ''}
          </pre>
        </div>
      )}

      {/* Timeline */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
        {versions.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            No checkpoints yet. Versions are saved automatically on each save.
          </div>
        )}
        {versions.map((v, i) => {
          const isSelected = selected === v.id
          const date = new Date(v.created_at)
          const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' })
          const isFirst = i === 0

          return (
            <div key={v.id} style={{ display: 'flex', gap: 10, padding: '0 14px', cursor: 'pointer' }} onClick={() => viewVersion(v.id)}>
              {/* Timeline line */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  background: isSelected ? 'var(--accent)' : isFirst ? 'var(--accent-2)' : 'var(--bg-3)',
                  border: `2px solid ${isSelected ? 'var(--accent)' : isFirst ? 'var(--accent-2)' : 'var(--border)'}`,
                  transition: 'all 0.15s',
                }} />
                {i < versions.length - 1 && <div style={{ width: 1, flex: 1, background: 'var(--border)' }} />}
              </div>
              {/* Content */}
              <div style={{
                flex: 1, paddingBottom: 12,
                background: isSelected ? 'rgba(124,110,245,0.06)' : 'none',
                borderRadius: 6, padding: '6px 8px', transition: 'background 0.12s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, color: isFirst ? 'var(--accent-2)' : 'var(--text-0)' }}>
                    {v.label ?? (v.auto ? 'Auto-save' : 'Manual save')}
                  </span>
                  {isFirst && <span style={{ fontSize: 8, color: 'var(--accent-2)', background: 'rgba(95,212,164,0.12)', padding: '1px 5px', borderRadius: 6, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>LATEST</span>}
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                  {dateStr} at {timeStr}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Hook: call this on every save to auto-checkpoint
export function useAutoCheckpoint() {
  return useCallback(async (filePath: string, content: string) => {
    try {
      await authFetch(apiUrl('/api/versions'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filePath, content, auto: true }),
      })
    } catch {}
  }, [])
}

const btnStyle: React.CSSProperties = {
  padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer',
  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600,
}
